/**
 * Team Leaderboard Display — staff performance
 * Vision spec: §4.1 — Revenue, Rating (stars), Utilization
 * Format: "1. Sarah EUR 4,200 * 4.9 92%"
 */
import { useSearchParams } from "react-router";
import { useEffect, useMemo, useState } from "react";
import { Trophy, AlertTriangle, Loader2, Users, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useDisplayData } from "@/hooks/use-display";
import type { LeaderboardData, DisplayStaffMember } from "@ease/shared";
import { formatCurrency, formatTime } from "@/lib/i18n";

function renderStars(rating: number): string {
  if (rating === 0) return "";
  return `${rating.toFixed(1)}`;
}

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
] as const;

export default function LeaderboardDisplay() {
  const [searchParams] = useSearchParams();
  const displayId = searchParams.get("displayId") ?? "";
  const token = searchParams.get("token") ?? "";
  const requestedPeriod = searchParams.get("period");
  const period = requestedPeriod === "week" || requestedPeriod === "month" ? requestedPeriod : "today";

  const { data, isLoading, error, isStale, lastUpdatedAt } = useDisplayData<LeaderboardData>(
    displayId,
    token,
    `leaderboard?period=${encodeURIComponent(period)}`,
  );
  const [previousRanks, setPreviousRanks] = useState<Record<string, number>>({});

  const staff: readonly DisplayStaffMember[] = data?.staff ?? [];

  useEffect(() => {
    if (staff.length === 0) {
      return;
    }

    setPreviousRanks((current) => {
      const next = { ...current };
      for (const [index, member] of staff.entries()) {
        next[member.resourceId] = index;
      }
      return next;
    });
  }, [staff]);

  const rankChanges = useMemo(() => {
    return Object.fromEntries(
      staff.map((member, index) => {
        const previousRank = previousRanks[member.resourceId];
        if (previousRank == null) {
          return [member.resourceId, 0];
        }

        return [member.resourceId, previousRank - index];
      }),
    );
  }, [previousRanks, staff]);

  if (!displayId || !token) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-12">
        <p className="text-2xl text-[var(--color-muted-foreground)]">Display not configured. Provide displayId and token.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-[var(--color-primary)]" aria-label="Loading display data" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center p-12">
        <div className="text-center space-y-4">
          <AlertTriangle className="h-12 w-12 mx-auto text-[var(--color-destructive)]" aria-label="Error" />
          <p className="text-2xl text-[var(--color-destructive)]">Unable to load leaderboard</p>
          <p className="text-lg text-[var(--color-muted-foreground)]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] p-12 flex flex-col items-center">
      {isStale && (
        <div className="w-full max-w-xl mb-6 rounded-xl bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/30 px-6 py-3 text-center">
          <p className="text-lg text-[var(--color-warning)]">Data may be outdated. Reconnecting...</p>
        </div>
      )}

      <div className="flex items-center gap-3 mb-10">
        <Trophy className="h-10 w-10 text-[var(--color-warning)]" aria-hidden="true" />
        <h1 className="text-5xl font-bold text-[var(--color-foreground)]">Team Leaderboard</h1>
      </div>

      <div className="mb-8 flex items-center gap-3 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] p-2">
        {PERIOD_OPTIONS.map((option) => {
          const isActive = option.value === period;
          const href = `?displayId=${encodeURIComponent(displayId)}&token=${encodeURIComponent(token)}&period=${option.value}`;
          return (
            <a
              key={option.value}
              href={href}
              className={`rounded-full px-5 py-2 text-base font-semibold transition ${
                isActive
                  ? "bg-[var(--color-primary)] text-[var(--color-primary-foreground)]"
                  : "text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
              }`}
            >
              {option.label}
            </a>
          );
        })}
      </div>

      <div className="w-full max-w-xl space-y-4">
        {staff.length === 0 && (
          <div className="text-center py-20">
            <Users className="h-16 w-16 mx-auto text-[var(--color-muted-foreground)] mb-4" aria-hidden="true" />
            <p className="text-2xl text-[var(--color-muted-foreground)]">No staff data for this period</p>
          </div>
        )}

        {staff.map((member, i) => {
          const rankDelta = rankChanges[member.resourceId] ?? 0;

          return (
            <div key={member.resourceId} className="flex items-center gap-6 rounded-2xl bg-[var(--color-surface)] px-8 py-6 border border-[var(--color-border)]">
              <span className={`text-3xl font-bold ${i === 0 ? "text-[var(--color-warning)]" : "text-[var(--color-muted-foreground)]"}`}>
                #{i + 1}
              </span>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-semibold text-[var(--color-foreground)]">{member.name}</div>
                  <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2 py-1 text-sm font-semibold text-[var(--color-muted-foreground)]">
                    {rankDelta > 0 && <TrendingUp className="h-4 w-4 text-[var(--color-success)]" aria-hidden="true" />}
                    {rankDelta < 0 && <TrendingDown className="h-4 w-4 text-[var(--color-destructive)]" aria-hidden="true" />}
                    {rankDelta === 0 && <Minus className="h-4 w-4" aria-hidden="true" />}
                    {rankDelta > 0 ? `+${rankDelta}` : rankDelta}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-lg text-[var(--color-muted-foreground)]">
                  <span>{member.bookings} bookings</span>
                  <span>{member.utilization}% utilized</span>
                  <span>{member.rating > 0 ? `${renderStars(member.rating)} stars` : "No rating yet"}</span>
                  <span>{formatCurrency(member.tips, "EUR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }, "en-IE")} tips</span>
                </div>
              </div>
              <div className="text-2xl font-bold text-[var(--color-success)]">
                {formatCurrency(member.revenue, "EUR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }, "en-IE")}
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-center mt-8 text-sm text-[var(--color-muted-foreground)]">
        Last updated: {lastUpdatedAt ? formatTime(lastUpdatedAt) : "..."}
      </div>
    </div>
  );
}
